import { Alert } from '@synoptic/ui-kit/alert/alert.tsx';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { GroupIcon } from '@synoptic/ui-kit/icons/react/group.tsx';
import { InfoCircleIcon } from '@synoptic/ui-kit/icons/react/info-circle.tsx';
import { LockSquareIcon } from '@synoptic/ui-kit/icons/react/lock-square.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { OnboardingLinkPage, StreamType } from '@/__generated__/graphql.ts';
import { pricingToggleItems } from '@/components/streams/stream-form/regular-stream-form/pricing-toggle-items.tsx';
import {
  streamFormActions,
  streamTypeStepContainer,
} from '@/components/streams/stream-form/stream-form.css.ts';
import { StreamFormInputs } from '@/components/streams/stream-form/stream-form.tsx';
import {
  StreamSelectionItem,
  StreamSelectionTagGroup,
} from '@/components/streams/stream-form/stream-selection-tag-group/stream-selection-tag-group.tsx';
import { StreamPricingType } from '@/components/streams/stream-pricing-type.tsx';
import { SetupStripeAccountModalContent } from '@/components/stripe-account/setup-stripe-account-modal-content.tsx';
import {
  StripeAccountStatus,
  useStripeAccountStatus,
} from '@/components/stripe-account/use-stripe-account-status.ts';

interface StreamTypeStepProps {
  streamPricingType: StreamPricingType;
  setStreamPricingType: (value: StreamPricingType) => void;
  onClose: () => void;
  onNext: () => void;
}

export const StreamTypeStep: FC<StreamTypeStepProps> = ({
  streamPricingType,
  setStreamPricingType,
  onClose,
  onNext,
}) => {
  const { status } = useStripeAccountStatus();
  const { control, setValue } = useFormContext<StreamFormInputs>();
  const streamType = useWatch({
    control,
    name: 'streamType',
  });
  const isTablet = useIsTablet();

  const typeToggleItems: StreamSelectionItem[] = useMemo(
    () => [
      {
        title: 'Private',
        value: StreamType.Private,
        icon: <LockSquareIcon />,
        description:
          'Visible only to you and invited users. Recommended for personal use or team sharing.',
        disabled: streamPricingType === StreamPricingType.PackageOnly,
      },
      {
        title: 'Public',
        value: StreamType.Public,
        icon: <GroupIcon />,
        description:
          'Visible to all Synoptic users. Recommended if you want to grow your audience or monetize the stream.',
      },
    ],
    [streamPricingType],
  );

  return (
    <div className={streamTypeStepContainer}>
      <StreamSelectionTagGroup
        onValueChange={(value) => {
          if (value) setStreamPricingType(value as StreamPricingType);

          // Package-only stream can only be public
          if (value === StreamPricingType.PackageOnly) {
            setValue('streamType', StreamType.Public);
          }
        }}
        defaultValue={StreamPricingType.Free}
        value={streamPricingType}
        items={pricingToggleItems}
        label={'Select the type of stream you’d like to create:'}
      />

      {streamPricingType === StreamPricingType.Paid &&
      status !== StripeAccountStatus.Configured ? (
        <SetupStripeAccountModalContent
          onClose={onClose}
          stripeAccountStatus={status}
          redirectTo={OnboardingLinkPage.Streams}
        />
      ) : (
        <>
          <Controller
            name={'streamType'}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <StreamSelectionTagGroup
                items={typeToggleItems}
                onValueChange={field.onChange}
                value={field.value}
                label={'Select the visibility of your stream:'}
              />
            )}
          />

          <Alert icon={<InfoCircleIcon />}>
            Type and visibility cannot be changed later.
          </Alert>

          <div className={streamFormActions}>
            <Button
              disabled={!streamType}
              type={'button'}
              size={isTablet ? 'large' : 'medium'}
              onClick={onNext}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
